import { useContext, useMemo } from 'react';
import { FeatureFlags, Filter } from '@fabric-ui/components';
import { checkIfSeverityFilter, sortBySeverity } from '@utils/filterUtils';
import { buildAliasResolution, getDimsWithAlias, mapAliasToName, parseWidgetDataBeforeApiRequest } from '@utils/utils';
import { useAvContext } from '../../../context/AvContextProvider';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { isHistoricalQuery, isValidTimeRange, shouldEnableWidgetQuery, shouldShowTotals } from '../CustomDashboardsUtils';
import { useQueryObjectWithHistoricalAsync } from '../hooks';
import { AllTypeWidgets, FeatureFlagsOverrides, HistoricalFilterMode } from '../types';

const getOverrideSortedData = (data: Record<string, string>[], widget) => {
  const severitySortField = widget.requests[0].select.dims.find(({ name }) => checkIfSeverityFilter(name));
  return sortBySeverity({
    data,
    severityFieldName: severitySortField?.name,
    key: severitySortField?.name,
    direction: widget.requests[0].sorting[0]?.dir,
  });
};

const isHistoricalEvaluationFilterMode = (widget: AllTypeWidgets, featureFlags: Record<string, unknown>) =>
  Boolean(
    featureFlags[FeatureFlags.WidgetHistoricalFilterMode] &&
    widget.historicalFilterMode === HistoricalFilterMode.HISTORICAL &&
    isHistoricalQuery(widget.requests[0])
  );

const contextFilterWithEvaluation = (widget: AllTypeWidgets, featureFlags: Record<string, boolean>, contextFilter?: Filter) =>
  isHistoricalEvaluationFilterMode(widget, featureFlags) && contextFilter ? { ...contextFilter, isHistorical: true } : contextFilter;

export const useGetWidgetData = ({
  widget,
  contextFilter,
  visible = true,
  debounceQueries = false,
}: {
  widget: AllTypeWidgets;
  contextFilter?: Filter;
  visible?: boolean;
  debounceQueries?: boolean;
}) => {
  const { featureFlags } = useAvContext();
  const { metricsWithAliases, globalInteractiveFilters } = useContext(CustomDashboardContext);

  const validTimeRange = isValidTimeRange(widget.requests?.[0]?.timeRange);
  const showTotals = shouldShowTotals(widget);
  const shouldEnableQuery = shouldEnableWidgetQuery({ widget });
  const enabled = validTimeRange && visible && !!widget.requests.length && shouldEnableQuery;

  const finalWidget = useMemo(
    () => parseWidgetDataBeforeApiRequest({ widget, metricsWithAliases }) as AllTypeWidgets,
    [widget, metricsWithAliases]
  );

  if (isHistoricalEvaluationFilterMode(widget, featureFlags)) {
    finalWidget.requests = finalWidget.requests.map(req => (req.filter ? { ...req, filter: { ...req.filter, isHistorical: true } } : req));
  }

  const aliasResolution = useMemo(
    () => buildAliasResolution({ dims: globalInteractiveFilters, contextFilter }),
    [globalInteractiveFilters, contextFilter]
  );

  const featureFlagsOverrides: FeatureFlagsOverrides = {};

  if (widget.binnedTimeBuckets) {
    featureFlagsOverrides.binnedTimeBuckets = true;
  }

  if (widget.queryAggIfCollapseOptimization) {
    featureFlagsOverrides.queryAggIfCollapseOptimization = true;
  }

  const results = useQueryObjectWithHistoricalAsync({
    queryObject: finalWidget.requests,
    showTotals,
    aliasResolution,
    contextFilter: contextFilterWithEvaluation(widget, featureFlags, contextFilter),
    totalRowCount: true,
    debounceQueries,
    options: { enabled },
    tags: { widgetId: widget.id },
    featureFlagsOverrides,
  });

  const {
    totals,
    data = [],
    isLoading,
    isRefetching,
    errors,
    totalRowCount,
    isAsyncQuery,
    isDebouncing,
    responseCalculatedAt,
    asyncQueryStatus,
    queryId,
  } = results;
  const isSeverityFilter = checkIfSeverityFilter(widget.requests[0].sorting[0]?.name || '');
  if (isSeverityFilter && !Array.isArray(data)) {
    console.warn('Non array severity data: ', data);
  }

  const dimsWithAlias = getDimsWithAlias(widget.requests[0].select.dims);

  const getParsedData = data => {
    const parsedData = mapAliasToName(data, dimsWithAlias);

    if (isSeverityFilter && Array.isArray(parsedData)) {
      return getOverrideSortedData(parsedData, widget);
    }

    return parsedData;
  };

  return {
    data: getParsedData(data),
    totals,
    isLoading,
    isRefetching,
    errors,
    totalRowCount,
    isAsyncQuery,
    isDebouncing,
    responseCalculatedAt,
    asyncQueryStatus,
    queryId,
  } as const;
};
